import { useState, useEffect, useMemo } from 'react';
import { useGetFeedTeamsQuery, useGetFeedFixturesQuery } from '@/api/feed';
import type { FeedFixture } from '@/api/feed/getFeedFixtures';
import { useGetFeedFixtureByIdQuery } from '@/api/feed/getFeedFixtureById';
import { formatInTimeZone } from 'date-fns-tz';
import type { SelectedFixtureData } from '@/components/forms/question/types';

interface UseQuestionFixtureProps {
  isNewQuestion: boolean;
  fixtureExternalId?: string | null;
  leagueId?: string;
  initialPickemId?: string | null;
  initialOptions?: Array<{
    grading_data?: {
      fixtureData?: {
        scheduledDate?: string;
      };
    };
  }>;
}

export function useQuestionFixture({
  isNewQuestion,
  fixtureExternalId,
  leagueId,
  initialOptions,
}: UseQuestionFixtureProps) {
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | undefined>(undefined);
  const [selectedFixtureData, setSelectedFixtureData] = useState<SelectedFixtureData | null>(null);

  // Get teams for logo generation and team mapping
  const { data: teams = [] } = useGetFeedTeamsQuery(leagueId);

  const teamMap = useMemo(() => {
    const map = new Map<string, { name: string; abbreviation?: string; market?: string }>();
    teams.forEach(team => {
      map.set(team.externalId, {
        name: team.name,
        abbreviation: team.abbreviation,
        market: team.market,
      });
    });
    return map;
  }, [teams]);

  // When loading an existing question with fixture_external_id, fetch fixture and populate selection
  // Check if scheduledDate is available in options (only over_under types store this currently)
  const hasFixtureDateInOptions = useMemo(() => {
    if (!isNewQuestion && initialOptions && initialOptions.length > 0) {
      return !!initialOptions[0]?.grading_data?.fixtureData?.scheduledDate;
    }
    return true; // new questions don't need date lookup
  }, [isNewQuestion, initialOptions]);

  // Fetch fixture by ID only when editing without fixtureData in options (team/player/custom types)
  const { data: fixtureByIdData } = useGetFeedFixtureByIdQuery(
    !isNewQuestion && !hasFixtureDateInOptions && !!fixtureExternalId
      ? fixtureExternalId
      : undefined
  );

  // Extract the scheduled date from the options to determine the date range for fixture loading
  const fixtureScheduledDate = useMemo(() => {
    if (!isNewQuestion && initialOptions && initialOptions.length > 0) {
      const firstOptionFixtureData = initialOptions[0]?.grading_data?.fixtureData;
      if (firstOptionFixtureData?.scheduledDate) {
        return new Date(firstOptionFixtureData.scheduledDate);
      }
    }
    if (fixtureByIdData?.scheduledDate) {
      return new Date(fixtureByIdData.scheduledDate);
    }
    return new Date();
  }, [isNewQuestion, initialOptions, fixtureByIdData]);

  // Only load fixtures once we have the correct date (avoids fetching today's fixtures needlessly)
  const hasCorrectFixtureDate = hasFixtureDateInOptions || !!fixtureByIdData?.scheduledDate;
  const shouldLoadFixture =
    !isNewQuestion && !!fixtureExternalId && !!leagueId && hasCorrectFixtureDate;

  const formattedFixtureScheduledDate = formatInTimeZone(
    fixtureScheduledDate,
    'America/New_York',
    'yyyy-MM-dd'
  );
  const { data: fixturesForLoad = [] } = useGetFeedFixturesQuery(
    shouldLoadFixture ? leagueId : undefined,
    undefined,
    formattedFixtureScheduledDate,
    formattedFixtureScheduledDate
  );

  useEffect(() => {
    if (
      !isNewQuestion &&
      fixtureExternalId &&
      leagueId &&
      fixturesForLoad.length > 0 &&
      !selectedFixtureId
    ) {
      const fixture = fixturesForLoad.find(f => f.externalId === fixtureExternalId);
      if (fixture) {
        const homeTeam = teamMap.get(fixture.homeTeamId);
        const awayTeam = teamMap.get(fixture.awayTeamId);
        const buildTeamDisplayName = (
          team: { name: string; market?: string } | undefined,
          fallback: string
        ) => {
          if (!team) return fallback;
          return team.market ? `${team.market} ${team.name}` : team.name;
        };
        const enhancedFixture: FeedFixture & {
          homeTeamName?: string;
          awayTeamName?: string;
          homeTeamAbbreviation?: string;
          awayTeamAbbreviation?: string;
        } = {
          ...fixture,
          homeTeamName: buildTeamDisplayName(homeTeam, fixture.homeTeamId),
          awayTeamName: buildTeamDisplayName(awayTeam, fixture.awayTeamId),
          homeTeamAbbreviation: homeTeam?.abbreviation || homeTeam?.name,
          awayTeamAbbreviation: awayTeam?.abbreviation || awayTeam?.name,
        };
        setSelectedFixtureId(fixtureExternalId);
        setSelectedFixtureData({ fixture: enhancedFixture, fixtureId: fixtureExternalId });
      }
    }
  }, [isNewQuestion, fixtureExternalId, leagueId, fixturesForLoad, selectedFixtureId, teamMap]);

  // Handle fixture selection
  const handleFixtureSelect = (
    fixtureId: string,
    fixture: FeedFixture & { homeTeamName?: string; awayTeamName?: string },
    formik?: any
  ) => {
    if (!fixtureId) {
      setSelectedFixtureId(undefined);
      setSelectedFixtureData(null);
      // Clear fixture_external_id and team_ids from formik if clearing selection
      if (formik) {
        formik.setFieldValue('fixture_external_id', null);
        formik.setFieldValue('team_ids', null);
      }
      return;
    }

    setSelectedFixtureId(fixtureId);
    setSelectedFixtureData({ fixture, fixtureId });
    // Store fixture external ID and team IDs in formik values for submission
    if (formik) {
      formik.setFieldValue('fixture_external_id', fixtureId);
      // Extract team IDs from fixture for feed-based question creation
      if (fixture.homeTeamId && fixture.awayTeamId) {
        formik.setFieldValue('team_ids', [fixture.homeTeamId, fixture.awayTeamId]);
      }
    }
  };

  return {
    selectedFixtureId,
    selectedFixtureData,
    teamMap,
    handleFixtureSelect,
  };
}
